// Mirror of Shared/Calculators/FlooringCalculator.cs.
import type { CalcDef, ResultLine } from "../types.js";
import * as F from "../format.js";

const fields: CalcDef["fields"] = [
  { key: "roomWidthFt", kind: "number", default: 12, min: 0.5, max: 200 },
  { key: "roomLengthFt", kind: "number", default: 14, min: 0.5, max: 200 },
  {
    key: "material",
    kind: "select",
    defaultChoice: "laminate",
    choices: [
      { value: "laminate", set: { boxCoverageSqft: 20.0 } },
      { value: "vinyl_plank", set: { boxCoverageSqft: 24.0 } },
      { value: "hardwood", set: { boxCoverageSqft: 20.0 } },
    ],
  },
  { key: "wastePct", kind: "number", default: 8, min: 0, max: 30 },
  { key: "pricePerSqft", kind: "number", default: 2.75, min: 0, max: 100 },
];

function compute(i: Record<string, number>): Record<string, number> {
  const w = F.get(i, "roomWidthFt");
  const l = F.get(i, "roomLengthFt");
  const boxCov = F.get(i, "boxCoverageSqft");
  const waste = F.get(i, "wastePct");
  const price = F.get(i, "pricePerSqft");

  const area = w * l;
  const areaWithWaste = area * (1 + waste / 100);
  const boxes = boxCov > 0 ? F.ceilUnits(areaWithWaste / boxCov) : 0;
  const boughtArea = boxes * boxCov;
  const cost = boughtArea * price;

  return {
    area: F.round(area, 2),
    areaWithWaste: F.round(areaWithWaste, 2),
    boxes,
    boughtArea: F.round(boughtArea, 2),
    cost: F.round(cost, 2),
  };
}

function present(i: Record<string, number>, r: Record<string, number>): ResultLine[] {
  const waste = F.get(i, "wastePct");
  const boxes = F.get(r, "boxes");
  return [
    { label: "Floor area", value: `${F.dec(F.get(r, "area"))} sq ft` },
    { label: `With ${F.dec(waste, 0)}% waste`, value: `${F.dec(F.get(r, "areaWithWaste"))} sq ft` },
    { label: "Boxes to buy", value: `${F.intStr(boxes)} ${F.plural(boxes, "box", "boxes")}`, highlight: true },
    { label: "Est. materials cost", value: F.usd(F.get(r, "cost")), sub: "rough — flooring only, excludes underlayment & labor" },
  ];
}

export const flooring: CalcDef = { slug: "flooring-calculator", fields, compute, present };
